import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryForm from "@/components/EnquiryForm";
import VideoShowcase from "@/components/VideoShowcase";
import LoopVisual from "@/components/LoopVisual";
import RoboSparkSpotlight from "@/components/RoboSparkSpotlight";
import Reveal from "@/components/Reveal";
import { ProductGrid } from "@/components/Products";
import { api, MEDIA } from "@/lib/api";

const ALL = "All";

export default function MartPage() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(ALL);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products").then((res) => setProducts(res.data)).catch(() => setProducts([])).finally(() => setLoading(false));
  }, []);

  const robots = useMemo(() => products.filter((p) => p.category === "Robots"), [products]);
  const deviceCategories = useMemo(
    () => [...new Set(products.filter((p) => p.category !== "Robots").map((p) => p.category))],
    [products]
  );
  const devices = useMemo(() => {
    let list = products.filter((p) => p.category !== "Robots");
    if (active !== ALL) list = list.filter((p) => p.category === active);
    if (query.trim()) list = list.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()));
    return list;
  }, [products, active, query]);

  const chips = [ALL, ...deviceCategories];

  return (
    <div className="bg-[#061536] text-white min-h-screen overflow-x-hidden" data-testid="mart-page">
      <Navbar />

      {/* MART HERO */}
      <section className="pt-16 pb-16 px-6 border-b border-[#00F0FF]/10">
        <div className="max-w-6xl mx-auto">
          <div className="font-mono-lbl text-[#00F0FF] text-xs uppercase tracking-widest mb-4 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#FF1E8C] animate-ping" />
            <span>AIoT Mart · Humanoid Robots + Automation Devices · Pan India</span>
          </div>
          <h1 className="font-disp text-5xl sm:text-7xl uppercase tracking-tight leading-[0.9] mb-6" data-testid="mart-headline">
            <span className="ln"><span>India's luxury</span></span>
            <span className="ln"><span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-[#CFF6FA] to-[#7FE3F0]"><span className="normal-case">AIoT</span> Mart</span></span>
          </h1>
          <p className="text-white/70 max-w-2xl text-sm sm:text-base mb-10">
            Humanoid robots trained for home and hotel tasks, and a full fleet of AI-enabled automation devices — all orchestrated from the LuxeRay Smart Homes app.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-7">
              <VideoShowcase startIndex={1} label="Featured Ad Film" hint="Tap for Sound" testid="mart-video" />
            </div>
            <Reveal className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-[#FF1E8C]/15 h-full min-h-[280px]" data-testid="mart-model-visual">
                <LoopVisual {...MEDIA.ambassadorMart} alt="LuxeRay ambassador with RoboSpark humanoid robots and the LuxeRay app" testid="mart-model-loop" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#061536] via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <span className="font-mono-lbl text-[11px] text-[#FF1E8C] uppercase tracking-wider block mb-1">Trained For Homes & Hotels</span>
                  <p className="text-white text-sm font-semibold">Robots that clean, serve, assist and secure — commanded from one app.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <RoboSparkSpotlight />

      {/* HUMANOID ROBOTS */}
      <section className="py-20 px-6 bg-[#040D26] border-b border-[#00F0FF]/10" data-testid="mart-robots-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
              <div>
                <span className="font-mono-lbl text-xs text-[#FF1E8C] uppercase tracking-widest block mb-2">Humanoid Fleet</span>
                <h2 className="font-disp text-4xl sm:text-5xl uppercase tracking-tight">Robots On Duty</h2>
              </div>
              <p className="text-white/70 max-w-md text-sm">Trained for cleaning, service, security and assistance across homes, hotels and offices.</p>
            </div>
          </Reveal>
          <ProductGrid products={robots} loading={loading} />
        </div>
      </section>

      {/* AUTOMATION DEVICES */}
      <section className="py-20 px-6" data-testid="mart-devices-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <span className="font-mono-lbl text-xs text-[#00F0FF] uppercase tracking-widest block mb-2">Automation Devices</span>
                <h2 className="font-disp text-4xl sm:text-5xl uppercase tracking-tight">AI-Enabled Hardware</h2>
              </div>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search devices..."
                className="bg-[#061536] border border-white/20 rounded-full px-5 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00F0FF] w-full md:w-64"
                data-testid="mart-search-input"
              />
            </div>
          </Reveal>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="mart-category-filters">
            {chips.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-4 py-2 rounded-full font-mono-lbl text-[11px] uppercase tracking-wider border transition-colors ${
                  active === c
                    ? "bg-[#00F0FF] text-[#061536] border-[#00F0FF] font-bold"
                    : "border-white/20 text-white/70 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]"
                }`}
                data-testid={`mart-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
          <ProductGrid products={devices} loading={loading} />
        </div>
      </section>

      <EnquiryForm />
      <Footer />
    </div>
  );
}
